import styled from '@emotion/styled'
import { Stack, Typography } from '@mui/material'
import isEmpty from 'lodash.isempty'
import React from 'react'

import { LoadWhenInView } from '@js/common/components/LoadWhenInView'
import { getUrlByDomain } from '@js/common/getUrlByDomain'

import { CTAButtonWithWT } from '@f-fertighaus/applications/Next/components/common/CTAButton'
import ErrorBoundary from '@f-fertighaus/applications/Next/components/common/ErrorBoundary'
import { HouseCarouselSkeletonLoader } from '@f-fertighaus/applications/Next/components/common/HouseCarouselSkeletonLoader'
import { InternalLinkWithWT } from '@f-fertighaus/applications/Next/components/common/InternalLink'
import {
  DesktopOnly,
  MobileOnly
} from '@f-fertighaus/applications/Next/components/common/MediaQuery'
import { HdpNavigationTab } from '@f-fertighaus/applications/Next/components/common/navigation/tabs/useActiveTabs'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import {
  ServiceAvailability,
  VendorDetails,
  VendorServices
} from '@f-fertighaus/applications/Next/types/vendorResponse'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { isNotEmptyArray } from '@f-fertighaus/applications/Next/utils/typeguards/isNotEmptyArray'
import { OtherVendorHousesCarousel } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/VendorDetailsSection/components/OtherVendorHousesCarousel'
import { ReviewsSection } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/VendorDetailsSection/components/ReviewsSection'
import { SalesOfficeForVendor } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/VendorDetailsSection/components/SalesOfficeForVendor'
import { VendorAwards } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/VendorDetailsSection/components/VendorAwards'
import { VendorLogoCtaSection } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/VendorDetailsSection/components/VendorLogoCtaSection'
import { VendorRatingSection } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/VendorDetailsSection/components/VendorRatingSection'
import { useUserPostcode } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/VendorDetailsSection/hooks/useUserPostcode'

import { GeneralVendorInfo } from './components/GeneralVendorInfo'
import { RangeOfService } from './components/RangeOfService'
import { VendorCoverImage } from './components/VendorCoverImage'

const Section = ({
  children,
  spacing = 1
}: {
  children: React.ReactNode
  spacing?: number
}) => <Stack spacing={spacing}>{children}</Stack>

const hasGeneralInfo = (vendor: VendorDetails): boolean =>
  Boolean(
    vendor.year_of_foundation ||
      vendor.number_of_houses ||
      vendor.houses_constructed_per_year ||
      vendor.houses_constructed_total ||
      vendor.number_of_employees
  )

export const hasRangeOfService = (
  services?: VendorServices
): services is VendorServices =>
  Boolean(
    services &&
      Object.values(services).some(
        (service: ServiceAvailability) => service === true
      )
  )

export const VendorDetailsSection = React.memo(() => {
  const { vendor, state, domain, reviewsActive } = useHouseDetail()
  const getUrl = getUrlByDomain(domain)

  const postcode = useUserPostcode()

  const isDisabled = !state.active

  return (
    <Stack>
      <VendorHeader>
        <VendorName
          variant='h5'
          component='h2'
          id={`navigation-category-${HdpNavigationTab.vendor}`}>
          {vendor.display_name}
        </VendorName>
        {state.active && (
          <InternalLinkWithWT
            href={getUrl('vendorDetail')(vendor.slug)}
            wtPayload={{
              category: 'CTA-link',
              action: 'click',
              label: 'view-vendor-profile',
              location: 'vendor-section',
              values: [
                {
                  type: 'text',
                  value: t('houseDetailPage.vendor.open-profile')
                },
                { type: 'url', value: getUrl('vendorDetail')(vendor.slug) }
              ]
            }}>
            {t('houseDetailPage.vendor.open-profile')}
          </InternalLinkWithWT>
        )}
      </VendorHeader>
      <DesktopOnly>
        {!isEmpty(vendor.cover_image) && (
          <VendorCoverImage coverImage={vendor.cover_image['fill-720x220']} />
        )}
      </DesktopOnly>
      <MobileOnly>
        {!isEmpty([vendor.cover_image_mobile, vendor.cover_image]) && (
          <VendorCoverImage
            coverImage={
              vendor.cover_image_mobile?.['fill-570x220'] ||
              vendor.cover_image['fill-720x220']
            }
          />
        )}
      </MobileOnly>
      {!isEmpty(state.review_data) && reviewsActive ? (
        <VendorRatingSection />
      ) : (
        <VendorLogoCtaSection />
      )}
      {hasGeneralInfo(vendor) && <GeneralVendorInfo vendor={vendor} />}
      <MobileOnly>
        <CTAButtonWithWT
          disabled={isDisabled}
          className='js-request-button'
          data-request-type='house-detail-form'
          data-funnel-variant='house-wizard-item-catalog'
          variant='contained'
          color='primary'
          wtPayload={{
            category: 'CTA-button',
            action: 'click',
            label: 'catalog-request',
            indicates: ['funnel-entry'],
            location: 'vendor-section',
            values: [
              {
                type: 'text',
                value: t('houseDetailPage.vendor.request-catalog')
              }
            ]
          }}
          {...(state.vendor_nid && { 'data-vendor-nid': state.vendor_nid })}>
          {t('houseDetailPage.vendor.request-catalog')}
        </CTAButtonWithWT>
      </MobileOnly>
      <ReviewsSection />
      <ServicesSection>
        {hasRangeOfService(vendor.services) && (
          <>
            <Typography variant='bodyTitle' marginBottom={2} component='p'>
              {t('houseDetailPage.vendor.range-of-services')}
            </Typography>
            <RangeOfService services={vendor.services} />
          </>
        )}
      </ServicesSection>
      {state.active && (
        <MobileOnly>
          <Typography variant='bodyTitle' component='p' marginBottom={2}>
            {t('houseDetailPage.vendor.personal-advice')}
          </Typography>
          {postcode ? (
            <SalesOfficeForVendor postcode={postcode} vendorNid={vendor.nid}>
              <Typography fontWeight='fontWeightBold' variant='body2'>
                {vendor.display_name}
              </Typography>
            </SalesOfficeForVendor>
          ) : (
            <SalesOfficeForVendor.DefaultContact>
              <Typography fontWeight='fontWeightBold' variant='body2'>
                {vendor.display_name}
              </Typography>
            </SalesOfficeForVendor.DefaultContact>
          )}
          <CTAButtonWithWT
            style={{
              marginTop: '16px',
              marginBottom: '24px'
            }}
            className='js-request-button'
            data-request-type='house-detail-form'
            data-funnel-variant='house-wizard-item-appointment'
            variant='contained'
            color='primary'
            wtPayload={{
              category: 'CTA-button',
              action: 'click',
              label: 'book-appointment',
              indicates: ['funnel-entry'],
              location: 'vendor-section',
              values: [
                {
                  type: 'text',
                  value: t('houseDetailPage.vendor.book-appointment')
                }
              ]
            }}>
            {t('houseDetailPage.vendor.book-appointment')}
          </CTAButtonWithWT>
        </MobileOnly>
      )}
      {isNotEmptyArray(vendor.vendor_awards) && (
        <>
          <Typography variant='bodyTitle' component='p'>
            {t('houseDetailPage.vendor.awards')}
          </Typography>
          <VendorAwards vendorAwards={vendor.vendor_awards} />
        </>
      )}
      {state.active && (
        <Section spacing={0}>
          <OtherVendorHousesTitle variant='bodyTitle' component='p'>
            {t('houseDetailPage.vendor.more-houses')}
          </OtherVendorHousesTitle>
          <OtherVendorHousesHeader>
            <Typography variant='body1'>
              {t('houseDetailPage.vendor.popular-houses-from-vendor', {
                vendor: vendor.display_name
              })}
            </Typography>
            <MobileOnly>
              <InternalLinkWithWT
                style={{ marginBottom: '16px' }}
                textVariant={InternalLinkWithWT.textVariant.bold}
                href={`${getUrl('houseList')()}?vendor_nid__in=${vendor.nid}`}
                wtPayload={{
                  category: 'CTA-link',
                  action: 'click',
                  label: 'view-all-houses',
                  location: 'vendor-section',
                  values: [
                    {
                      type: 'text',
                      value: t(
                        'houseDetailPage.vendor.all-houses-from-vendor',
                        {
                          vendor: vendor.display_name
                        }
                      )
                    },
                    {
                      type: 'url',
                      value: `${getUrl('houseList')()}?vendor_nid__in=${
                        vendor.nid
                      }`
                    }
                  ]
                }}>
                {t('houseDetailPage.vendor.all-houses-from-vendor', {
                  vendor: vendor.display_name
                })}
              </InternalLinkWithWT>
            </MobileOnly>
            <DesktopOnly>
              <InternalLinkWithWT
                textVariant={InternalLinkWithWT.textVariant.bold}
                href={`${getUrl('houseList')()}?vendor_nid__in=${vendor.nid}`}
                wtPayload={{
                  category: 'CTA-link',
                  action: 'click',
                  label: 'view-all-houses',
                  location: 'vendor-section',
                  values: [
                    {
                      type: 'text',
                      value: t('houseDetailPage.vendor.all-houses')
                    },
                    {
                      type: 'url',
                      value: `${getUrl('houseList')()}?vendor_nid__in=${
                        vendor.nid
                      }`
                    }
                  ]
                }}>
                {t('houseDetailPage.vendor.all-houses')}
              </InternalLinkWithWT>
            </DesktopOnly>
          </OtherVendorHousesHeader>
          <ErrorBoundary>
            <LoadWhenInView Placeholder={() => <HouseCarouselSkeletonLoader />}>
              <OtherVendorHousesCarousel />
            </LoadWhenInView>
          </ErrorBoundary>
        </Section>
      )}
    </Stack>
  )
})

VendorDetailsSection.displayName = 'VendorDetailsTab'

const VendorHeader = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing(1, 0)};
  flex-direction: column;
  label: VendorHeader;

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }

  & > a {
    margin-bottom: ${({ theme }) => theme.spacing(1)};
    margin-top: ${({ theme }) => theme.spacing(1)};
  }
`
const VendorName = styled(Typography)`
  margin: ${({ theme }) => theme.spacing(1, 1, 0, 0)};
  label: VendorName;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${({ theme }) => theme.spacing(1)};
  }
` as typeof Typography

const ServicesSection = styled.div`
  margin: ${({ theme }) => theme.spacing(3, 0, 1, 0)};
  label: ServicesSection;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin: ${({ theme }) => theme.spacing(4, 0)};
  }
`

const OtherVendorHousesHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  label: OtherVendorHousesHeader;

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
  }
`

const OtherVendorHousesTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${({ theme }) => theme.spacing(1)};
  }
` as typeof Typography
